import { z } from 'zod'
import { featureKeySchema } from '../entitlements/feature-key.schema'
import { planTierSchema } from '../entitlements/plan-tier.schema'
import { themeKeySchema } from '../entitlements/theme-key.schema'

export const releaseTypeSchema = z.enum(['FEATURE', 'IMPROVEMENT', 'FIX'])
export const releaseStageSchema = z.enum(['COMING_SOON', 'RELEASED', 'PULLED'])
export const publishStateSchema = z.enum([
  'DRAFTED',
  'WAITING_ON_RELEASE',
  'PUBLISHED',
  'HELD',
  'WITHDRAWN',
])
export const scopeLevelSchema = z.enum(['BRAND', 'LOCATION', 'BOTH'])
export const contentAudienceSchema = z.enum(['EXTERNAL', 'INTERNAL'])
export const deliveryStageSchema = z.enum([
  'MERGED_MAIN',
  'ON_STAGING',
  'PARTIAL_PROD',
  'IN_PROD',
  'REVERTED',
])
export const gateTypeSchema = z.enum(['FEATURE_FLAG', 'CAPABILITY'])
export const gateSourceSchema = z.enum(['DETECTED', 'LINEAR_PROJECT', 'MANUAL'])
export const flagStateSchema = z.enum(['OFF', 'TARGETED', 'FULL', 'REMOVED_FROM_CODE'])
export const noteArticleRelationSchema = z.enum(['PRIMARY', 'UPDATED', 'RELATED'])
export const changeRoleSchema = z.enum(['PRIMARY', 'FOLLOW_UP', 'FIX'])
export const editMethodSchema = z.enum(['AI_GENERATE', 'MANUAL', 'RESTORE'])
export const contentTypeSchema = z.enum(['RELEASE_NOTE', 'KNOWLEDGE_ARTICLE', 'CHANGELOG_ENTRY'])
export const releaseEventTypeSchema = z.enum([
  'MERGED',
  'STAGED',
  'CUT',
  'DEPLOYED_PROD',
  'FLAG_CHANGED',
  'FLAG_REMOVED',
  'CAPABILITY_RELEASED',
  'GATE_OPENED',
  'GATE_CLOSED',
  'AUTO_PUBLISHED',
  'EDITED',
  'HELD',
  'WITHDRAWN',
  'REVERTED',
  'NOTIFIED',
])

export const MAX_HIGHLIGHTS = 3

export const screenshotSchema = z.object({
  url: z.url(),
  caption: z.string().min(1).max(160),
  alt: z.string().max(300),
})

export const releaseNoteSchema = z.object({
  releaseNoteId: z.uuid(),
  slug: z.string().min(1).max(255),
  title: z.string().min(1).max(255),
  releaseType: releaseTypeSchema,
  area: z.string().max(100).nullable(),
  scopeLevel: scopeLevelSchema,
  stage: releaseStageSchema,
  publishState: publishStateSchema,
  customerFacing: z.boolean(),
  tldr: z.string().max(500),
  lead: z.string().nullable(),
  bullets: z.array(z.string().max(300)).max(10),
  whoImpacted: z.string().nullable(),
  highlights: z.array(z.string().min(1).max(80)).max(MAX_HIGHLIGHTS),
  screenshots: z.array(screenshotSchema).max(12),
  capabilityKey: featureKeySchema.nullable(),
  gateFlagKey: z.string().max(120).nullable(),
  releaseItemId: z.uuid().nullable(),
  linearProjectId: z.string().nullable(),
  linkedPrs: z.array(z.number().int().positive()),
  roleTags: z.array(z.string().max(50)),
  relatedReleaseNoteIds: z.array(z.uuid()),
  humanEdited: z.boolean(),
  releasedAt: z.date().nullable(),
  publishedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
})

export const releaseNoteArticleSchema = z.object({
  releaseNoteId: z.uuid(),
  knowledgeArticleId: z.uuid(),
  relation: noteArticleRelationSchema,
  sortOrder: z.number().int(),
})

export const releaseNoteEnablementSchema = z.object({
  releaseNoteId: z.uuid(),
  whyBuilt: z.string().nullable(),
  idealCustomers: z.string().nullable(),
  howToSell: z.string().nullable(),
  howToDemo: z.string().nullable(),
  knownLimitations: z.string().nullable(),
  updatedBy: z.string().nullable(),
  updatedAt: z.date(),
})

export const releaseItemSchema = z.object({
  releaseItemId: z.uuid(),
  itemKey: z.string().regex(/^RI-\d{4}-\d{4}$/),
  title: z.string().min(1).max(255),
  releaseType: releaseTypeSchema,
  customerFacing: z.boolean(),
  scopeLevel: scopeLevelSchema,
  area: z.string().max(100).nullable(),
  deliveryStage: deliveryStageSchema,
  linearIssueIds: z.array(z.string()),
  linearProjectId: z.string().nullable(),
  hideComingSoon: z.boolean(),
  stagedAt: z.date().nullable(),
  prodAt: z.date().nullable(),
  releasedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
})

export const releaseItemChangeSchema = z.object({
  releaseItemId: z.uuid(),
  repo: z.string().min(1),
  prNumber: z.number().int().positive(),
  role: changeRoleSchema,
  title: z.string(),
  linearIssueId: z.string().nullable(),
  mergedToMainAt: z.date(),
  onStagingAt: z.date().nullable(),
  inProdAt: z.date().nullable(),
  prodCut: z.string().nullable(),
  revertedAt: z.date().nullable(),
})

export const releaseGateSchema = z.object({
  releaseItemId: z.uuid(),
  gateId: z.uuid(),
  gateType: gateTypeSchema,
  gateKey: z.string().min(1),
  htcCode: z
    .string()
    .regex(/^HTC-\d{4}$/)
    .nullable(),
  source: gateSourceSchema,
  flagState: flagStateSchema.nullable(),
  capabilityReleased: z.boolean().nullable(),
  catalogVersion: z.string().nullable(),
  lastSyncedAt: z.date().nullable(),
})

export const releaseEventSchema = z.object({
  releaseItemId: z.uuid(),
  eventId: z.uuid(),
  eventType: releaseEventTypeSchema,
  actor: z.string(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.date(),
})

export const changelogEntrySchema = z.object({
  changelogEntryId: z.uuid(),
  audience: contentAudienceSchema,
  entryType: releaseTypeSchema,
  line: z.string().min(1).max(300),
  releaseItemId: z.uuid().nullable(),
  releaseNoteId: z.uuid().nullable(),
  knowledgeArticleId: z.uuid().nullable(),
  prNumber: z.number().int().positive().nullable(),
  linearIssueId: z.string().nullable(),
  prodCut: z.string().nullable(),
  occurredAt: z.date(),
})

export const contentVersionSchema = z.object({
  contentType: contentTypeSchema,
  contentId: z.uuid(),
  version: z.number().int().positive(),
  snapshot: z.record(z.string(), z.unknown()),
  editMethod: editMethodSchema,
  editedBy: z.string(),
  changeNote: z.string().nullable(),
  createdAt: z.date(),
})

export const viewerAvailabilitySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('COMING_SOON') }),
  z.object({ kind: z.literal('AVAILABLE') }),
  z.object({
    kind: z.literal('UPGRADE'),
    requiredPlan: planTierSchema.nullable(),
    requiredTheme: themeKeySchema.nullable(),
  }),
  z.object({ kind: z.literal('CONTACT_US') }),
])

export const monthKeySchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/)

export const monthCountSchema = z.object({
  month: monthKeySchema,
  count: z.number().int().nonnegative(),
})

export const releaseFeedEntrySchema = z.object({
  id: z.uuid(),
  slug: z.string().min(1),
  stage: releaseStageSchema,
  changedAt: z.iso.datetime(),
})

export const releaseFeedSchema = z.object({
  version: z.string().min(1),
  entries: z.array(releaseFeedEntrySchema),
})

export const releaseReadStateSchema = z.object({
  readKeys: z.array(z.string()).max(500),
  readAllBefore: z.iso.datetime().nullable(),
})

export type ReleaseType = z.infer<typeof releaseTypeSchema>
export type ReleaseStage = z.infer<typeof releaseStageSchema>
export type PublishState = z.infer<typeof publishStateSchema>
export type ScopeLevel = z.infer<typeof scopeLevelSchema>
export type ContentAudience = z.infer<typeof contentAudienceSchema>
export type NoteArticleRelation = z.infer<typeof noteArticleRelationSchema>
export type Screenshot = z.infer<typeof screenshotSchema>
export type ReleaseNote = z.infer<typeof releaseNoteSchema>
export type ReleaseNoteArticle = z.infer<typeof releaseNoteArticleSchema>
export type ReleaseNoteEnablement = z.infer<typeof releaseNoteEnablementSchema>
export type ReleaseItem = z.infer<typeof releaseItemSchema>
export type ReleaseItemChange = z.infer<typeof releaseItemChangeSchema>
export type ReleaseGate = z.infer<typeof releaseGateSchema>
export type ReleaseEvent = z.infer<typeof releaseEventSchema>
export type ChangelogEntry = z.infer<typeof changelogEntrySchema>
export type ContentVersion = z.infer<typeof contentVersionSchema>
export type ViewerAvailability = z.infer<typeof viewerAvailabilitySchema>
export type MonthKey = z.infer<typeof monthKeySchema>
export type MonthCount = z.infer<typeof monthCountSchema>
export type ReleaseFeedEntry = z.infer<typeof releaseFeedEntrySchema>
export type ReleaseFeed = z.infer<typeof releaseFeedSchema>
export type ReleaseReadState = z.infer<typeof releaseReadStateSchema>
